/**
 * Canvas-rail controls — Canvas On/Off + Undo + Delete-selected
 * (M7.5 C1 / C2 / C3). Ports the legacy `coach-draw-actions` row
 * (`js/coaching/review.js:923-936`).
 *
 * Used by both `<TacticalBoardEditor>` and `<TelestratorCanvas>`
 * with the same shape — each editor owns the state (visibility,
 * undo stack, selection) and wires it through these props.
 */

export type CanvasRailControlsProps = {
  /** Whether the drawing layer is visible (Canvas On). */
  visible: boolean;
  /** Toggle the visible flag. */
  onToggleVisible: () => void;
  /** Stack depth — drives the Undo button's `disabled` state. */
  undoDepth: number;
  /** Pop the last snapshot and apply it to the scene. */
  onUndo: () => void;
  /** True iff a shape/token is currently selected — drives
   * Delete-selected's `disabled` state. */
  hasSelection: boolean;
  /** Remove the selected shape/token. */
  onDeleteSelected: () => void;
  /** Optional testid prefix — defaults to `"canvas-rail"`. The
   * three buttons get `${prefix}-toggle-visible` / `${prefix}-undo`
   * / `${prefix}-delete-selected`. */
  testidPrefix?: string;
};

export function CanvasRailControls(props: CanvasRailControlsProps) {
  const prefix = props.testidPrefix ?? "canvas-rail";
  return (
    <div
      className="flex flex-wrap gap-1.5"
      role="group"
      aria-label="Canvas controls"
      data-testid={prefix}
    >
      <button
        type="button"
        onClick={props.onToggleVisible}
        aria-pressed={props.visible}
        data-testid={`${prefix}-toggle-visible`}
        className={[
          "rounded border px-2 py-1 text-xs font-display uppercase tracking-wide transition-colors",
          props.visible
            ? "border-accent bg-accent/15 text-ink"
            : "border-rule bg-surface text-ink-muted hover:text-ink",
        ].join(" ")}
      >
        Canvas {props.visible ? "On" : "Off"}
      </button>
      <button
        type="button"
        onClick={props.onUndo}
        disabled={props.undoDepth === 0}
        data-testid={`${prefix}-undo`}
        title={`Undo (${props.undoDepth} step${props.undoDepth === 1 ? "" : "s"})`}
        className="rounded border border-rule bg-surface text-ink-muted hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 text-xs font-display uppercase tracking-wide transition-colors"
      >
        Undo
      </button>
      <button
        type="button"
        onClick={props.onDeleteSelected}
        disabled={!props.hasSelection}
        data-testid={`${prefix}-delete-selected`}
        className="rounded border border-destructive/40 text-destructive disabled:opacity-40 disabled:cursor-not-allowed hover:bg-destructive/10 px-2 py-1 text-xs font-display uppercase tracking-wide transition-colors"
      >
        Delete
      </button>
    </div>
  );
}
